/* eslint-disable react/destructuring-assignment */
/* eslint-disable sort-class-members/sort-class-members */
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { isEqual } from 'lodash'
import { Player } from './Player'
import { getVideoPlayerProgress } from './helpers/getVideoPlayerProgress'
import { isMediaServerUrl } from '~/helpers/isMediaServerUrl'

const NETWORK_IDLE = 1
const BUFFERING_TIMEOUT_MS = 10_000

const mapToPlayerCallback = cb => player => {
  if (player?.target) {
    cb(player.target)
  }
}

class ExtendedReactPlayer extends Component {

  constructor(props) {
    super(props)

    this.player = {
      isReady: false,
      duration: null,
      buffering: {
        isBuffering: false,
        progress: null,
      },
      getInternalPlayer: () => this.videoPlayer,
      getDuration: () => this.player.duration || (this.videoPlayer?.duration || 0),
      getCurrentTime: () => this.videoPlayer?.currentTime || 0,
      getId: () => this.props.id,
      getMode: () => this.props.mode,
      seekTo: seconds => {
        if (this.videoPlayer && this.videoPlayer.currentTime !== seconds) {
          this.videoPlayer.currentTime = seconds
          // This is necessary so that the preview slider doesn't freeze when rewinding the video
          this.props.onProgress(this.player)
        }
      },
    }

    this.handleWaitingMapped = mapToPlayerCallback(this.onWaiting)
    this.handleStalledMapped = mapToPlayerCallback(() => {})
    this.handleReadyMapped = mapToPlayerCallback(this.handleReady)
    this.handleProgressInternalMapped = mapToPlayerCallback(this.handleProgressInternal)
    this.handlePausedMapped = mapToPlayerCallback(this.handlePaused)
    this.handleCanPlayThroughMapped = mapToPlayerCallback(this.handleCanPlayThrough)
    this.handleSeekingMapped = mapToPlayerCallback(this.handleSeeking)
    this.handleSeekedMapped = mapToPlayerCallback(this.handleSeeked)
    this.handlePlayingMapped = mapToPlayerCallback(this.handlePlay)

    this.lastUpdateTime = 0
    this.isUnmounted = false
  }

  bufferingTimeout = null
  playingProgressInterval = null
  videoPlayer = null
  onBufferingStartTime = 0
  nonStopId = null
  nonStopWait = false

  componentDidMount() {
    this.isUnmounted = false
    this.videoPlayer = this.props.innerRef.current

    if (this.videoPlayer) {
      this.handleLoadStart()
    }

    this.props.onMounted(this.player)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.mode !== this.props.mode) {
      this.clearTimeouts()
      this.nonStopWait = false
      this.handleEndBuffering()
    }

    if (!isEqual(prevProps, this.props)) {
      this.handleUpdate()
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true
    this.clearTimeouts()
    this.handleEndBuffering()

    if (this.videoPlayer) {
      this.videoPlayer.pause()
      this.videoPlayer.removeAttribute('src')
      this.videoPlayer.load()
    }
    this.videoPlayer = null
  }

  get isPlayerBuffering() {
    if (!this.videoPlayer) return false
    const { readyState, buffered } = this.videoPlayer

    if (buffered.length && buffered.end(0) >= this.player.getDuration()) {
      return false
    }

    return (readyState < 3) || !buffered.length
  }

  isEnded = () => this.videoPlayer.ended
    || (this.videoPlayer.currentTime >= this.player.getDuration() && this.player.getDuration() > 0)

  handleUpdate = () => {
    if (!this.videoPlayer || this.isUnmounted) return

    if (this.videoPlayer.volume !== this.props.volume) {
      this.videoPlayer.volume = this.props.volume ?? 0
    }
    if (this.videoPlayer.muted !== this.props.muted) {
      this.videoPlayer.muted = this.props.muted
    }

    const { isBackground, playing } = this.props
    const { isBuffering } = this.player.buffering

    if (!this.videoPlayer.paused && !playing) {
      this.videoPlayer.pause()
    } else if (playing) {
      if (this.videoPlayer.paused) {
        this.videoPlayer.play().catch(() => { /* NOP */ })
      }

      if (this.player.isReady && !isBuffering && !isBackground && !this.playingProgressInterval) {
        this.startProgressAnimation()
      }
    }
  }

  handleTimeUpdate = () => {
    if (this.isUnmounted) return

    const { playing, isPrimary, isBackground, progressInterval } = this.props
    const { isBuffering } = this.player.buffering

    if (!playing || isBuffering || !this.videoPlayer
      || !this.player.isReady || !isPrimary || isBackground) {
      this.stopProgressAnimation()
      return
    }

    if (this.isEnded()) {
      this.handlePaused()
      return
    }

    const currentTime = performance.now()
    if ((currentTime - this.lastUpdateTime) >= progressInterval) {
      this.lastUpdateTime = currentTime
      this.props.onProgress(this.player, { isBackground, id: this.props.id })
    }

    this.playingProgressInterval = window.requestAnimationFrame(this.handleTimeUpdate)
  }

  startProgressAnimation = () => {
    if (this.playingProgressInterval) return
    this.playingProgressInterval = window.requestAnimationFrame(this.handleTimeUpdate)
  }

  stopProgressAnimation = () => {
    window.cancelAnimationFrame(this.playingProgressInterval)
    this.playingProgressInterval = null
  }

  handleStartBuffering = () => {
    if (this.isUnmounted) return
    const { buffering } = this.player
    const { expectedBufferingS } = this.props

    const { progress } = !expectedBufferingS
      ? { progress: 0 }
      : getVideoPlayerProgress(this.videoPlayer, expectedBufferingS)

    if (!buffering.isBuffering && progress <= 99) {
      buffering.isBuffering = true
      buffering.progress = progress

      this.stopProgressAnimation()
      this.resetBufferingTimeout()
      this.props.onBufferingProgress()
    }
  }

  handlePlay = () => {
    this.handleEndBuffering()
  }

  handleStartBufferingNonStop = () => {
    if (this.isUnmounted) return

    if (Date.now() - this.onBufferingStartTime < 500) return
    this.onBufferingStartTime = Date.now()

    clearTimeout(this.nonStopId)

    if (this.props.active && this.isPlayerBuffering) {
      this.player.buffering.isBuffering = true
      this.nonStopWait = true

      const curTime = this.player.getCurrentTime()
      const dur = this.player.getDuration()
      const seekTime = Math.min(curTime + 10, dur)

      this.player.seekTo(seekTime)
      this.nonStopId = setTimeout(() => {
        if (!this.isPlayerBuffering) {
          this.nonStopWait = false
          this.handleEndBuffering()
        } else {
          this.handleStartBufferingNonStop()
        }
      }, 10000)
    } else {
      this.nonStopWait = false
      this.handleEndBuffering()
    }
    this.props.onBufferingProgress()
  }

  handleEndBuffering = () => {
    if (this.props.mode === 'test' && this.nonStopWait) return false

    if (this.player.buffering.isBuffering) {
      this.player.buffering.isBuffering = false
      this.player.buffering.progress = 100

      clearTimeout(this.bufferingTimeout)
      clearTimeout(this.nonStopId)

      this.props.onBufferingProgress()
      this.handleUpdate()
      return true
    }
    return false
  }

  resetBufferingTimeout = () => {
    clearTimeout(this.bufferingTimeout)
    this.bufferingTimeout = setTimeout(() => {
      if (!this.isPlayerBuffering) {
        this.handleEndBuffering()
      } else {
        this.resetBufferingTimeout()
      }
    }, BUFFERING_TIMEOUT_MS)
  }

  clearTimeouts = () => {
    this.stopProgressAnimation()
    clearTimeout(this.bufferingTimeout)
    clearTimeout(this.nonStopId)
  }

  handleProgressInternal = videoPlayer => {
    const { buffering } = this.player
    if (!buffering.isBuffering) return

    const readyThreshold = isMediaServerUrl(this.props.url) ? 2 : 3

    if (this.videoPlayer.readyState >= readyThreshold) {
      this.handleEndBuffering()
      return
    }

    if (this.props.expectedBufferingS) {
      const { progress } = getVideoPlayerProgress(videoPlayer, this.props.expectedBufferingS)
      if (progress > 99) {
        this.handleEndBuffering()
      } else {
        buffering.progress = progress
        this.resetBufferingTimeout()
      }
    }
  }

  handleReady = () => {
    this.player.isReady = true
    const duration = this.player.getDuration()
    this.player.duration = duration
    this.props.onReady(this.player)
    this.props.onDuration(duration)
    this.handleUpdate()
  }

  handleLoadStart = () => {
    if (!this.props.expectedBufferingS) {
      if (this.props.mode === 'test') {
        this.handleStartBufferingNonStop()
      } else {
        this.handleStartBuffering()
      }
    }
  }

  handlePaused = () => {
    this.props.onProgress(this.player, {
      isBackground: this.props.isBackground,
    })
    this.stopProgressAnimation()
  }

  handleEnded = () => {
    this.props.onEnded()
  }

  handleOnDuration = duration => {
    this.player.duration = duration
    this.props.onDuration(duration)
  }

  handleSeeking = () => {
    if (this.videoPlayer?.networkState === NETWORK_IDLE && !this.isPlayerBuffering) {
      this.handleEndBuffering()
    } else if (this.props.mode !== 'test') {
      this.handleStartBuffering()
    }
  }

  handleSeeked = () => {
    if (this.isUnmounted) return

    const readyThreshold = isMediaServerUrl(this.props.url) ? 2 : 3

    let bufferingEnded = false
    if (this.videoPlayer.readyState >= readyThreshold) {
      bufferingEnded = this.handleEndBuffering()
    }

    this.props.onProgress(this.player, {
      isBackground: !this.props.isPrimary,
    })

    if (!bufferingEnded) {
      this.handleUpdate()
    }
  }

  handleCanPlayThrough = () => {
    if (!this.props.expectedBufferingS && !this.isPlayerBuffering) {
      this.handleEndBuffering()
    }
  }

  onWaiting = () => {
    if (this.props.mode === 'test') {
      this.handleStartBufferingNonStop()
    } else {
      this.handleStartBuffering()
    }
  }

  render() {
    const {
      id, url, innerRef, addTimelinePlayers, width, height,
      playAudioOnly, crossOrigin, controls, muted, className,
    } = this.props

    return (
      <Player
        className={className}
        src={url}
        id={id}
        crossOrigin={crossOrigin}
        ref={innerRef}
        width={width}
        height={height}
        playAudioOnly={playAudioOnly}
        controls={controls}
        muted={muted}
        onWaiting={this.handleWaitingMapped}
        onStalled={this.handleStalledMapped}
        onLoadedMetadata={this.handleReadyMapped}
        onProgress={this.handleProgressInternalMapped}
        onPause={this.handlePausedMapped}
        onEnded={this.handleEnded}
        onCanPlayThrough={this.handleCanPlayThroughMapped}
        onSeeking={this.handleSeekingMapped}
        onSeeked={this.handleSeekedMapped}
        onDuration={this.handleOnDuration}
        onPlaying={this.handlePlayingMapped}
        addTimelinePlayers={addTimelinePlayers}
      />
    )
  }

}

ExtendedReactPlayer.defaultProps = {
  id: '',
  playing: false,
  volume: 0,
  expectedBufferingS: null,
  progressInterval: 50,
  crossOrigin: 'anonymous',
  controls: true,
  muted: false,
  mode: 'default',
  active: false,
  playAudioOnly: false,
  onProgress: () => {},
  onReady: () => {},
  onDuration: () => {},
  onBufferingProgress: () => {},
  onMounted: () => {},
  onEnded: () => {},
  addTimelinePlayers: () => {},
  isBackground: false,
  isPrimary: false,
}

ExtendedReactPlayer.propTypes = {
  id: PropTypes.string,
  url: PropTypes.string.isRequired,
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  playAudioOnly: PropTypes.bool,
  playing: PropTypes.bool,
  volume: PropTypes.number,
  expectedBufferingS: PropTypes.number,
  progressInterval: PropTypes.number,
  crossOrigin: PropTypes.string,
  controls: PropTypes.bool,
  muted: PropTypes.bool,
  mode: PropTypes.oneOf([ 'default', 'test' ]),
  active: PropTypes.bool,
  onProgress: PropTypes.func,
  onReady: PropTypes.func,
  onDuration: PropTypes.func,
  onBufferingProgress: PropTypes.func,
  onMounted: PropTypes.func,
  onEnded: PropTypes.func,
  addTimelinePlayers: PropTypes.func,
  isBackground: PropTypes.bool,
  isPrimary: PropTypes.bool,
}

export default React.forwardRef((props, ref) => (
  <ExtendedReactPlayer innerRef={ref} {...props} />
))
